import { supabase } from '../lib/supabase';

// Achievement type mappings with display-friendly data
const ACHIEVEMENT_METADATA = {
  top_creator: {
    name: 'Top Creator',
    description: 'Created highly-rated models loved by the community',
    icon: 'Trophy',
    color: 'text-yellow-500'
  },
  community_favorite: {
    name: 'Community Favorite',
    description: 'Received exceptional community engagement',
    icon: 'Heart',
    color: 'text-red-500'
  },
  quality_contributor: {
    name: 'Quality Contributor',
    description: 'Consistently delivers high-quality models',
    icon: 'Star',
    color: 'text-blue-500'
  },
  early_adopter: {
    name: 'Early Adopter',
    description: 'One of the first members of the platform',
    icon: 'Zap',
    color: 'text-purple-500'
  },
  model_collector: {
    name: 'Model Collector',
    description: 'Built an impressive collection of models',
    icon: 'Package',
    color: 'text-green-500'
  },
  master_builder: {
    name: 'Master Builder',
    description: 'Created complex and innovative assemblies',
    icon: 'Award',
    color: 'text-orange-500'
  }
};

export const achievementService = {
  /**
   * Get user's achievements with display metadata
   * Now gets userId internally from Supabase auth
   */
  async getUserAchievements(userId = null) {
    try {
      // Get userId from auth if not provided
      let effectiveUserId = userId;
      if (!effectiveUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) throw new Error('Not authenticated');
        effectiveUserId = user?.id;
      }

      const { data, error } = await supabase
        ?.from('user_achievements')?.select('id, achievement_type, earned_at')?.eq('user_id', effectiveUserId)?.order('earned_at', { ascending: false });

      if (error) throw error;

      // Map achievements to include display-friendly metadata
      return (data || [])?.map(achievement => {
        const metadata = ACHIEVEMENT_METADATA?.[achievement?.achievement_type] || {
          name: 'Unknown Achievement',description: 'Achievement details not available',icon: 'Award',color: 'text-gray-500'
        };

        return {
          id: achievement?.id,
          achievementType: achievement?.achievement_type,
          earnedAt: achievement?.earned_at,
          name: metadata?.name,
          description: metadata?.description,
          icon: metadata?.icon,
          color: metadata?.color
        };
      });
    } catch (error) {
      console.error('Error fetching achievements:', error);
      throw error;
    }
  },

  /**
   * Award an achievement to a user
   */
  async awardAchievement(userId, achievementType) {
    try {
      // Validate achievement type exists
      if (!ACHIEVEMENT_METADATA?.[achievementType]) {
        throw new Error(`Invalid achievement type: ${achievementType}`);
      }

      const { data, error } = await supabase
        ?.from('user_achievements')
        ?.insert({
          user_id: userId,
          achievement_type: achievementType
        })
        ?.select()
        ?.single();

      if (error) {
        // If duplicate error (already has this achievement), return null
        if (error?.code === '23505') {
          return null;
        }
        throw error;
      }

      // Return with metadata
      const metadata = ACHIEVEMENT_METADATA?.[achievementType];
      return {
        id: data?.id,
        achievementType: data?.achievement_type,
        earnedAt: data?.earned_at,
        name: metadata?.name,
        description: metadata?.description,
        icon: metadata?.icon,
        color: metadata?.color
      };
    } catch (error) {
      console.error('Error awarding achievement:', error);
      throw error;
    }
  },

  // Award achievement to user (admin function)
  async awardToUser(userId, achievementId, notes = '') {
    const { data: { user: currentUser } } = await supabase?.auth?.getUser();
    if (!currentUser) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_achievements')?.insert({
        user_id: userId,
        achievement_id: achievementId,
        awarded_by: currentUser?.id,
        notes: notes
      })?.select(`
        *,
        achievements (
          id,
          name,
          description,
          icon,
          points,
          achievement_categories (
            id,
            name,
            icon
          )
        )
      `)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      achievementId: data?.achievement_id,
      achievementName: data?.achievements?.name,
      achievementDescription: data?.achievements?.description,
      achievementIcon: data?.achievements?.icon,
      achievementPoints: data?.achievements?.points,
      categoryName: data?.achievements?.achievement_categories?.name,
      awardedBy: data?.awarded_by,
      notes: data?.notes,
      earnedAt: data?.earned_at
    };
  },

  // Remove achievement from user
  async removeFromUser(userAchievementId) {
    const { error } = await supabase?.from('user_achievements')?.delete()?.eq('id', userAchievementId);

    if (error) throw error;
  },

  // Get all user achievements with full details (ADMIN SYSTEM)
  async getUserAchievementsWithDetails(userId) {
    const { data, error } = await supabase?.from('user_achievements')?.select(`
        *,
        achievements (
          id,
          name,
          description,
          icon,
          points,
          achievement_categories (
            id,
            name,
            icon
          )
        ),
        awarded_by_user:users!user_achievements_awarded_by_fkey (
          id,
          first_name,
          last_name,
          email
        )
      `)?.eq('user_id', userId)?.order('earned_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      achievementId: row?.achievement_id,
      achievementName: row?.achievements?.name,
      achievementDescription: row?.achievements?.description,
      achievementIcon: row?.achievements?.icon,
      achievementPoints: row?.achievements?.points,
      categoryName: row?.achievements?.achievement_categories?.name,
      categoryIcon: row?.achievements?.achievement_categories?.icon,
      awardedBy: row?.awarded_by,
      awardedByName: row?.awarded_by_user ? 
        `${row?.awarded_by_user?.first_name} ${row?.awarded_by_user?.last_name}` : null,
      awardedByEmail: row?.awarded_by_user?.email,
      notes: row?.notes,
      earnedAt: row?.earned_at
    }));
  },

  // Get achievement statistics for user
  async getUserStats(userId) {
    const { data, error } = await supabase?.from('user_achievements')?.select(`
        id,
        achievements (
          points,
          achievement_categories (
            name
          )
        )
      `)?.eq('user_id', userId);

    if (error) throw error;

    const totalPoints = data?.reduce((sum, achievement) => 
      sum + (achievement?.achievements?.points || 0), 0);
    
    const totalAchievements = data?.length;
    
    const byCategory = data?.reduce((acc, achievement) => {
      const categoryName = achievement?.achievements?.achievement_categories?.name || 'Unknown';
      acc[categoryName] = (acc?.[categoryName] || 0) + 1;
      return acc;
    }, {});

    return {
      totalPoints,
      totalAchievements,
      byCategory
    };
  },

  /**
   * Remove an achievement from a user
   */
  async removeAchievement(achievementId) {
    try {
      const { error } = await supabase
        ?.from('user_achievements')
        ?.delete()
        ?.eq('id', achievementId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing achievement:', error);
      throw error;
    }
  },

  /**
   * Check if user has a specific achievement
   */
  async hasAchievement(userId, achievementType) {
    try {
      const { data, error } = await supabase
        ?.from('user_achievements')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('achievement_type', achievementType)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking achievement:', error);
      return false;
    }
  }
};